/**
 * Helper: Format
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 * - Add in alphabetical order.
 *
 */
import { formatMoney } from '@shopify/theme-currency'

import { convertToHandle } from '~/helpers/convert'
import { decodeApiId, transformEdges } from '~/helpers/graphql'

/**
 * Formats response from product.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatProduct(response) {
  let product = response

  if (response.product) {
    product = response.product
  }

  const url = cnvs.routes.root === '/'
    ? `//${location.host}/products/${product.handle}`
    : `//${location.host}${cnvs.routes.root}/products/${product.handle}`
  const formattedProduct = {
    available: product.available,
    compareAtPrice: formatPrice(product.compareAtPriceRange),
    description: product.descriptionHtml,
    handle: product.handle,
    id: decodeApiId(product.id),
    images: formatImages(product),
    options: formatOptions(product),
    price: formatPrice(product.priceRange),
    tags: product.tags,
    title: product.title,
    type: product.productType,
    vendor: product.vendor,
    url,
  }

  /**
   * Adding metafield values.
   */
  formattedProduct.videoUrl = product.videoUrl?.value
  formattedProduct.origin = product.origin?.value
  formattedProduct.swatchHexes = product.swatchHexes?.value

  const directionsArray = []
  const keyFeaturesArray = []

  if (product.directions?.value) {
    const directionsJson = JSON.parse(product.directions?.value)
    directionsJson.forEach(value => directionsArray.push(value))
  }

  if (product.keyFeatures?.value) {
    const keyFeaturesJson = JSON.parse(product.keyFeatures?.value)
    keyFeaturesJson.forEach(value => keyFeaturesArray.push(value))
  }

  if (directionsArray.length) {
    formattedProduct.directions = [...directionsArray]
  }

  if (keyFeaturesArray.length) {
    formattedProduct.keyFeatures = [...keyFeaturesArray]
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.giftCard = formattedProduct.type === 'Gift Cards'
  formattedProduct.variants = formatVariants(product, formattedProduct.giftCard)

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  return formattedProduct
}

/**
 * Formats price.
 * @param {Object} price - Price response.
 * @returns {Object}
 */
function formatPrice(price) {
  return {
    amount: (Number(price?.minVariantPrice.amount) * 100) ?? false,
    currencyCode: price?.minVariantPrice.currencyCode,
    max: (Number(price?.maxVariantPrice.amount) * 100) ?? false,
    min: (Number(price?.minVariantPrice.amount) * 100) ?? false,
    varies: (price?.maxVariantPrice.amount !== price?.minVariantPrice.amount),
  }
}

/**
 * Formats images.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatImages(product) {
  return transformEdges(product.images).map((image) => {
    const linkedVariant = transformEdges(product.variants).find((variant) => {
      return variant.image.id === image.id
    })

    return {
      alt: image.altText,
      height: image.height,
      id: decodeApiId(image.id),
      src: image.url,
      variant: decodeApiId(linkedVariant?.id) || false,
      width: image.width,
    }
  })
}

/**
 * Formats options.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatOptions(product) {
  return product.options.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values,
    }
  })
}

/**
 * Formats variants.
 * @param {Object} response - GraphQL response.
 * @param {Boolean} isGiftCard - Product type is gift card.
 * @returns {Array}
 */
function formatVariants(product, isGiftCard) {
  return transformEdges(product.variants).map((variant) => {
    const formattedVariant = {
      available: variant.available,
      compareAtPrice: {
        amount: (Number(variant.compareAtPriceV2?.amount) * 100) || false,
        currencyCode: variant.compareAtPriceV2?.currencyCode || false,
      },
      id: decodeApiId(variant.id),
      image: {
        alt: variant.image?.altText,
        id: decodeApiId(variant.image?.id) || false,
        url: variant.image?.url || false,
        height: variant.image?.height || false,
        width: variant.image?.altText || false,
      },
      inventory: variant.quantityAvailable,
      price: {
        amount: (Number(variant.priceV2.amount) * 100),
        currencyCode: variant.priceV2.currencyCode,
      },
      selectedOptions: variant.selectedOptions.map((option) => {
        return {
          name: option.name,
          value: option.value,
        }
      }),
      sku: variant.sku,
      colorHexCode: variant.colorHexCode?.value,
      ingredients: variant.ingredients?.value,
    }

    const includedArray = []

    if (variant.included?.value) {
      const includedJson = JSON.parse(variant.included?.value)
      includedJson.forEach(value => includedArray.push(value?.value))
    }

    if (includedArray.length) {
      formattedVariant.included = [...includedArray]
    }

    /**
     * Want for prices to be formatted before determining title.
     * - Gift cards show the price as their title for multi-currency purposes.
     */
    formattedVariant.title = isGiftCard
      ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat)
      : variant.title

    return formattedVariant
  })
}

/**
 * Formats response from collection.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatCollection(response) {
  const collection = response.collection

  const url = cnvs.routes.root === '/'
    ? `//${location.host}/collections/${collection.handle}`
    : `//${location.host}${cnvs.routes.root}/collections/${collection.handle}`

  const formattedCollection = {
    description: collection.descriptionHtml,
    handle: collection.handle,
    id: decodeApiId(collection.id),
    title: collection.title,
    url,
  }

  /**
   * Format products.
   */
  formattedCollection.products = transformEdges(collection.products).map((productResponse) => {
    const product = productResponse

    return {
      handle: product.handle,
    }
  })

  return formattedCollection
}

/**
 * Formats response from menu.
 * @param {String} [prefix] - Prefix to prepend to handle to make it unique.
 * @param {String} [previousHandle] - Previous tier's handle.
 * @param {Object} response - GraphQL response or nested items array.
 * @param {Number} [tier] - Current tier number.
 * @returns {Array}
 */
export function formatMenu({
  prefix = 'mobile',
  previousHandle,
  response,
  tier = 1,
} = {}) {
  const menu = response?.menu?.items.length ? response.menu.items : response

  return menu.map((object, index) => {
    const handle = previousHandle
      ? `${prefix}-${previousHandle}-${convertToHandle(object.title)}-${tier}-${index}`
      : `${prefix}-${convertToHandle(object.title)}-${tier}-${index}`

    const links = object.items?.length
      ? formatMenu({
          prefix,
          previousHandle: handle,
          response: object.items,
          tier: (tier + 1),
        })
      : []

    return {
      handle,
      links,
      tier,
      title: object.title,
      url: object.url,
    }
  })
}

/**
 * Get a size image URL for Shopify's CDN.
 * @param {String} src - Image URL.
 * @param {String} size - Image size filter.
 * @returns {String}
 */
export function getSizedImageUrl(src, size) {
  if (size === null || size === 'master') {
    return src
  }

  const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i)

  if (match) {
    const prefix = src.split(match[0])
    const suffix = match[0]

    return `${prefix[0]}_${size}${suffix}`
  }

  return null
}

/**
 * Format Algolia product data.
 * @param {Object} response - Algolia item data.
 * @returns {Object} formatted data.
 */
export function formatAlgoliaProduct(response) {
  const product = response

  const url = cnvs.routes.root === '/'
    ? `//${location.host}/products/${product.handle}`
    : `//${location.host}${cnvs.routes.root}/products/${product.handle}`

  const compareAtPrice = product.compare_at_price
    ? Number(product.compare_at_price * 100)
    : false

  const formattedProduct = {
    ...product, // Keep all the original Algolia fields
    available: product.inventory_available || product.inventory_management === null,
    compareAtPrice,
    description: product.body_html_safe,
    handle: product.handle,
    id: product.id,
    images: formatAlgoliaImages(product),
    options: null,
    price: formatAlgoliaPrice(product),
    tags: formatAlgoliaTags(product),
    title: product.title,
    type: product.product_type,
    vendor: product.vendor,
    variantId: Number(product.objectID) ?? false,
    url,
  }

  formattedProduct.featuredImage = formattedProduct.images[0]

  return formattedProduct
}

/**
 * Format Algolia product price.
 * @param {Object} product - Algolia item data.
 * @returns {Object} formatted price data.
 */
function formatAlgoliaPrice(product) {
  return {
    amount: (Number(product.variants_min_price) * 100) ?? false,
    currencyCode: window.cnvs.store.currency,
    max: (Number(product.variants_max_price) * 100) ?? false,
    min: (Number(product.variants_min_price) * 100) ?? false,
    varies: (product.variants_max_price !== product.variants_min_price),
  }
}

/**
 * Format Algolia image data.
 * @param {Object} product - Algolia item data.
 * @returns {Array} formatted image data as an array of objects.
 */
function formatAlgoliaImages(product) {
  return [
    {
      alt: product.title,
      src: product.image,
    },
  ]
}

/**
 * Format the product tags from Algolia.
 * @param {Object} product - Algolia item data.
 * @returns {Array} array of product tags.
 */
function formatAlgoliaTags(product) {
  const tags = [...product.tags]
  const namedTags = namedTagsToTags(product)
  const allTags = tags.concat(namedTags)

  return allTags
}

/**
 * Convert Algolia's named tags to tags.
 * - Loops through the `named_tags` object and restores the tags back to simple
 *   text tags as entered in the Shopify admin.
 * @param {Object} product - Algolia item data.
 * @returns {Array} array of product tags.
 */
function namedTagsToTags(product) {
  const convertedTags = []

  Object.entries(product.named_tags).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((arrayValue) => {
        convertedTags.push(`${key}:${arrayValue}`)
      })

      return
    }

    convertedTags.push(`${key}:${value}`)
  })

  return convertedTags
}

/**
 * Format Algolia facet name to a readable format.
 * - Splits the facet name on the `.` character and then capitalizes the first
 *  letter of each word.
 * @param {String} string - Algolia facet name.
 * @returns {String} formatted facet name.
 */
export function formatAlgoliaFilterTitle(string) {
  let title = string

  title = string
    .split('.')
    .pop()
    .replace(/-/g, ' ')
    .replace(/_/g, ' ')
    .replace(
      /\w\S*/g,
      (word) => titleCase(word),
    )

  return title
}

/**
 * Convert string to Title Case.
 * @param {String} string - String.
 * @returns {String}
 */
function titleCase(string) {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
}
